/* FICHIER: DashboardPage.jsx — version avec modale “Mon compte” centrée
   - OTA / online/offline : inchangé (sauf OTA UI)
   - OTA UI: spinner indéterminé tant que pas de pourcentage, barre dès qu’on reçoit pct
   - Modale “Mon compte” : remplit username / email / created_at
   - Déconnexion depuis la modale
   - Bouton "Changer le mot de passe" (redirige, à adapter)
*/
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import DeviceCard from './DeviceCard';
import DeviceListBox from './DeviceListBox';
// import AccountMenu from './AccountMenu'; // plus utilisé (modale centrée)
import NewDeviceWizard from './NewDeviceWizard';
import { API_BASE } from '../config/api';
import client from '../mqttService';
import Select from 'react-select';
import { FlagIcon } from 'react-flag-kit';
import { FaUserCircle } from 'react-icons/fa';
import ConsoChart from "./ConsoChart"; // adapte le chemin si ton fichier est ailleurs

// CSS externes
import './dashboard.css';
import './modals.css';

// ===== Debug switch =====
const DEBUG = false;
const dlog  = (...a) => DEBUG && console.log(...a);
const dwarn = (...a) => DEBUG && console.warn(...a);

// Fenêtre de grâce offline
const OFFLINE_GRACE_MS = 90_000;
// Pop-up (ON/OFF)
const POPUP_AUTO_HIDE_MS = 30_000;
// Vérif firmware
const FW_CHECK_IV_MS = 20 * 60 * 1000;
// Fenêtre OTA pour accepter "start/queued"
const OTA_WINDOW_MS = 180000;

const FW_GET_CURRENT = (model) =>
  `${API_BASE}/firmware/get_current.php?model=${encodeURIComponent(model || '')}&ts=${Date.now()}`;
const FW_GET_LATEST = (model) =>
  `${API_BASE}/firmware/get_latest.php?model=${encodeURIComponent(model || '')}&ts=${Date.now()}`;
const FW_REQUEST_OTA = `${API_BASE}/firmware/request_ota.php`;
const FW_OTA_PROGRESS = null; // (HTTP polling optionnel, non utilisé ici)

const COUNTRY_OPTIONS = [
  { value: 'FR', label: 'France', flag: 'FR' },
  { value: 'IT', label: 'Italie', flag: 'IT' },
  { value: 'ES', label: 'Espagne', flag: 'ES' },
  { value: 'DE', label: 'Allemagne', flag: 'DE' },
  { value: 'US', label: 'États-Unis', flag: 'US' },
  { value: 'GB', label: 'Royaume-Uni', flag: 'GB' },
  { value: 'CA', label: 'Canada', flag: 'CA' },
  { value: 'MA', label: 'Maroc',       flag: 'MA' },
];

const normalizeMac = (s) => String(s || '').replace(/[^a-fA-F0-9]/g, '').toUpperCase();
const extractVersion = (s) => {
  const m = String(s || '').match(/(\d+\.\d+\.\d+)/);
  return m ? m[1] : '';
};
const extractModel = (s) => {
  const m = String(s || '').match(/^([A-Za-z0-9_-]+)[ _-]\d+\.\d+\.\d+/);
  return m ? m[1].replace(/-+/g, '_') : '';
};
const cmpVer = (a, b) => {
  const pa = String(a || '').split('.').map(n => parseInt(n, 10) || 0);
  const pb = String(b || '').split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) {
    if (pa[i] > pb[i]) return 1;
    if (pa[i] < pb[i]) return -1;
  }
  return 0;
};
const parseLatestFromResp = (j) => {
  const name = j?.latest_firmware_name || j?.file || j?.name || '';
  return j?.latest_version || j?.version || extractVersion(name) || '';
};

// -------- helpers UI compte --------
function safeToLocaleDate(val) {
  if (!val) return '—';
  // Gérer format SQL "YYYY-MM-DD HH:MM:SS"
  const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const [, y, mo, d] = m;
    return `${d}-${mo}-${y}`;
  }
  // Sinon, fallback ISO ou autre
  const isoish = String(val).replace(' ', 'T');
  const d = new Date(isoish);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

async function fetchJSON(url) {
  const r = await fetch(url, { credentials: 'include', headers: { 'Cache-Control': 'no-cache' }});
  let j = null;
  try { j = await r.json(); } catch {}
  return { ok: r.ok, json: j };
}


// ===== OTA helpers (nouvelle logique spinner → barre) =====
function extractPercent(text) {
  const m = String(text || '').match(/(\d{1,3})\s*%/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (Number.isFinite(n)) return Math.max(0, Math.min(100, n));
  return null;
}
function parseOtaStatusMessage(msgStr) {
  try {
    const j = JSON.parse(msgStr);
    const state = String(j?.state || j?.status || '').toLowerCase();
    const progress = j?.progress ?? j?.percent ?? j?.percentage ?? j?.pct;
    return { state, progress };
  } catch {
    const s = String(msgStr || '').trim().toLowerCase();
    const p = extractPercent(s) ?? undefined;
    let state = 'unknown';
    if (s.includes('queued')) state = 'queued';
    else if (s.includes('start')) state = 'start';
    else if (s.includes('download')) state = 'download';
    else if (s.includes('flash')) state = 'flash';
    else if (s.includes('reboot')) state = 'reboot';
    else if (s.includes('success') || s.includes('done')) state = 'success';
    else if (s.includes('error') || s.includes('failed')) state = 'error';
    return { state, progress: p };
  }
}

function mapOtaProgress(payload) {
  const state = String(payload?.state || payload?.status || '').toLowerCase();
  const raw = payload?.progress ?? payload?.percent ?? payload?.percentage ?? payload?.pct;
  const hasPct = raw != null && !Number.isNaN(Number(raw));
  const p = hasPct ? Math.max(0, Math.min(100, Number(raw))) : 0;

  // Indéterminé tant que l’ESP n’a pas publié de pourcentage
  const indeterminate = !hasPct && ['queued', 'start', 'download'].includes(state);

  if (state === 'done' || state === 'success') {
    return { progress: 100, status: 'done', updating: false, indeterminate: false };
  }
  if (state === 'error' || state === 'failed') {
    return { progress: p, status: 'error', updating: false, indeterminate: false, error: 'ota_failed' };
  }
  if (state === 'reboot') {
    return { progress: hasPct ? Math.max(95, p) : 95, status: 'reboot', updating: true, indeterminate: false };
  }
  if (state === 'flash') {
    return { progress: hasPct ? Math.max(80, p) : 80, status: 'flash', updating: true, indeterminate: false };
  }
  // queued / start / download (sans pct) => spinner
  if (indeterminate) {
    return { progress: 0, status: state, updating: true, indeterminate: true };
  }
  // Sinon, on a un chiffre (download avec pct, etc.)
  return { progress: p, status: state || 'unknown', updating: p < 100, indeterminate: false };
}

const openOtaWindow = (ref, mac, ms = OTA_WINDOW_MS) => { ref.current[mac] = Date.now() + ms; };

function flattenDevices(groupedDevices) {
  if (!Array.isArray(groupedDevices)) return [];
  return groupedDevices.flatMap(group => Array.isArray(group.devices) ? group.devices : []);
}

function DashboardPage({ user, onLogout, onDeviceClick, isGuest = false }) {
  // responsive
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleResize = () => windowWidth !== window.innerWidth && setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [windowWidth]);

  const [myDevices, setMyDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [relayStates, setRelayStates] = useState({});
  const [onlineStates, setOnlineStates] = useState({});

  //===== graphique de conso Tasmota ======
  const [showConsoChart, setShowConsoChart] = useState(false);
  const [consoHistory, setConsoHistory] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [consoRange, setConsoRange] = useState('all'); // 'all' | 'week' | 'day'
  const filteredConsoHistory = useMemo(() => {
    if (!consoHistory || !consoHistory.length) return [];

    if (consoRange === 'all') return consoHistory;

    const now = Date.now();

    if (consoRange === 'day') {
      const oneDayMs = 24 * 60 * 60 * 1000;
      return consoHistory.filter(point => {
        const t = new Date(point.date).getTime();
        return now - t <= oneDayMs;
      });
    }

    if (consoRange === 'week') {
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      return consoHistory.filter(point => {
        const t = new Date(point.date).getTime();
        return now - t <= sevenDaysMs;
      });
    }

    return consoHistory;
  }, [consoHistory, consoRange]);

  // UI états
  const [selectedClaimedId, setSelectedClaimedId] = useState(null);
  const [showWizard, setShowWizard] = useState(false);

  // === Compte (modale centrée) ===
  const [accountOpen, setAccountOpen] = useState(false);

  const [showPwModal, setShowPwModal] = useState(false);
  const [oldPw, setOldPw] = useState('');

  // Suppression de compte — modal 3 étapes
  const [delStep, setDelStep]       = useState(0); // 0=caché 1=avert 2=typer 3=mdp
  const [delTyped, setDelTyped]     = useState('');
  const [delPassword, setDelPassword] = useState('');
  const [delLoading, setDelLoading] = useState(false);
  const [delError, setDelError]     = useState('');
  const resetDel = () => { setDelStep(0); setDelTyped(''); setDelPassword(''); setDelError(''); };
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [account, setAccount] = useState({ username: '', email: '', created_at: '' });
  const [accountLoading, setAccountLoading] = useState(false);

  // === Membres du foyer ===
  const [members, setMembers]               = useState([]);
  const [inviteEmail, setInviteEmail]       = useState('');
  const [inviteStatus, setInviteStatus]     = useState('');
  const [loadingInvite, setLoadingInvite]   = useState(false);
  const [showMembersSection, setShowMembersSection] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({});

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Commande vocale
  const [isListening, setIsListening]   = useState(false);
  const [voiceText,   setVoiceText]     = useState('');
  const voiceRecRef = useRef(null);

  // === Tasmota (modale de consommation) ===
  const [selectedTasmota, setSelectedTasmota] = useState(null);
  const [energyData, setEnergyData] = useState(null);
  
  // 🔹 Charger les données dès qu’une prise est sélectionnée
useEffect(() => {
  if (selectedTasmota) {
    setEnergyData(null); // reset avant chargement

    fetch(`/backend/devices/energy_conso.php?mac=${encodeURIComponent(selectedTasmota.identifier)}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.ok && data.energyData) {
          setEnergyData(data.energyData);
        } else {
          setEnergyData(null);
        }
      })
      .catch(err => {
        console.error("Erreur chargement energy_conso :", err);
        setEnergyData(null);
      });
  }
}, [selectedTasmota]);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);
  const localActionRef = useRef({});
  const prevRelayRef = useRef({});
  const playDing = useCallback((freq = 880) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t0 = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.3, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
      osc.start();
      osc.stop(t0 + 0.2);
      setTimeout(() => ctx.close(), 300);
    } catch {}
  }, []);

  const showNotification = useCallback((name, isOn) => {
    try {
      if (!('Notification' in window)) return;
      const send = () => {
        new Notification('🌿 Lumieres de Jardin', {
          body: `${name} — ${isOn ? 'Allumé' : 'Éteint'}`,
          icon: '/logo-fond-transparent.png',
          silent: false
        });
      };
      if (Notification.permission === 'granted') {
        send();
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(p => { if (p === 'granted') send(); });
      }
    } catch {}
  }, []);

  const showToast = useCallback((id, name, isOn) => {
    if (toastTimerRef.current) { clearTimeout(toastTimerRef.current); toastTimerRef.current = null; }
    setToast({ id, name, isOn });
    playDing(isOn ? 900 : 600);
    showNotification(name, isOn);
    toastTimerRef.current = setTimeout(() => setToast(null), POPUP_AUTO_HIDE_MS);
  }, [playDing, showNotification]);

  // mapping MAC -> id
  const macToDeviceId = useMemo(() => {
    const mapping = {};
    flattenDevices(myDevices).forEach(device => {
      const mac = normalizeMac(device?.identifier);
      if (mac) mapping[mac] = device.id;
    });
    return mapping;
  }, [myDevices]);
  const idToName = useMemo(() => {
    const map = {};
    flattenDevices(myDevices).forEach(d => {
      const label = d?.name || d?.device_name || `Appareil ${d?.id}`;
      map[String(d?.id)] = label;
      map[Number(d?.id)] = label;
    });
    return map;
  }, [myDevices]);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const otaWindowRef = useRef({}); // { [MAC]: expiresTs }

  // --------- ONLINE polling ---------
  useEffect(() => {
    function softMergeOnline(fromApiArray) {
      const now = Date.now();
      setOnlineStates(prev => {
        let changed = false;
        const next = { ...prev };
        fromApiArray.forEach(({ id, online }) => {
          const incoming = Number(online) === 1;
          const prevEntry = prev[id];
          if (!prevEntry) {
            next[id] = { online: incoming, lastSeen: incoming ? now : 0 };
            changed = true;
            return;
          }
          if (incoming) {
            if (!prevEntry.online || now - prevEntry.lastSeen > 0) {
              next[id] = { online: true, lastSeen: now };
              changed = true;
            }
          } else {
            if (prevEntry.online && now - prevEntry.lastSeen > OFFLINE_GRACE_MS) {
              next[id] = { online: false, lastSeen: prevEntry.lastSeen };
              changed = true;
            }
          }
        });
        return changed ? next : prev;
      });
    }
    function fetchOnlineStates() {
      fetch(`${API_BASE}/devices/get_relays_state.php`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => Array.isArray(data) && softMergeOnline(data))
        .catch(e => console.error('[OnlineStates] fetch error:', e));
    }
    fetchOnlineStates();
    const interval = setInterval(fetchOnlineStates, 120000);
    return () => clearInterval(interval);
  }, []);

  // --------- DEVICES polling ---------
  const fetchMyDevices = useCallback(() => {
    return fetch(`${API_BASE}/devices/get_my_devices.php`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        setMyDevices(prev => {
          try { return JSON.stringify(prev) === JSON.stringify(data) ? prev : data; }
          catch { return data; }
        });
        setRelayStates(prev => {
          const next = { ...prev };
          flattenDevices(data).forEach(d => {
            if (typeof next[d.id] === 'undefined' && typeof d.relay_state !== 'undefined') {
              next[d.id] = d.relay_state === 1 || d.relay_state === '1';
            }
          });
          return next;
        });
        const now = Date.now();
        setOnlineStates(prev => {
          let changed = false;
          const next = { ...prev };
          flattenDevices(data).forEach(d => {
            const incoming = Number(d.online) === 1;
            const prevEntry = prev[d.id];
            if (!prevEntry) {
              next[d.id] = { online: incoming, lastSeen: incoming ? now : 0 };
              changed = true;
              return;
            }
            if (incoming) {
              if (!prevEntry.online || now - prevEntry.lastSeen > 0) {
                next[d.id] = { online: true, lastSeen: now };
                changed = true;
              }
            } else {
              if (prevEntry.online && now - prevEntry.lastSeen > OFFLINE_GRACE_MS) {
                next[d.id] = { online: false, lastSeen: prevEntry.lastSeen };
                changed = true;
              }
            }
          });
          return changed ? next : prev;
        });
      })
      .catch(e => console.error('[MyDevices] fetch error:', e));
  }, []);
  const myDevicesRef = useRef([]);
  useEffect(() => { myDevicesRef.current = myDevices; }, [myDevices]);
  useEffect(() => {
    fetchMyDevices().finally(() => setLoadingDevices(false));
    const interval = setInterval(fetchMyDevices, 60000);
    return () => clearInterval(interval);
  }, [fetchMyDevices]);

  // MQTT base
  useEffect(() => {
    if (!client) {
      console.error('[MQTT][Dashboard] client MQTT undefined');
      return;
    }
    const onConnect = () => dlog('[MQTT][Dashboard] Connecté');
    const onClose   = () => dwarn('[MQTT][Dashboard] Fermé');
    const onError   = (err) => console.error('[MQTT][Dashboard] Erreur MQTT:', err);
    const onOffline = () => dwarn('[MQTT][Dashboard] Offline');
    client.on('connect', onConnect);
    client.on('close', onClose);
    client.on('error', onError);
    client.on('offline', onOffline);
    return () => {
      client.off('connect', onConnect);
      client.off('close', onClose);
      client.off('error', onError);
      client.off('offline', onOffline);
    };
  }, []);

  // ========= MQTT: state + status (Sonoff + Tasmota) =========
useEffect(() => {
  if (!client) return;

  const macs = flattenDevices(myDevices)
    .map(d => normalizeMac(d?.identifier))
    .filter(Boolean);

  const topics = [];
  macs.forEach(mac => {
    topics.push(`sonoff/state/${mac}`);
    topics.push(`sonoff/status/${mac}`);
  });

  // Abonnements Tasmota : ON/OFF + LWT + STATE périodique
  topics.push('tasmota/stat/+/POWER');
  topics.push('tasmota/stat/+/RESULT');
  topics.push('tasmota/tele/+/LWT');
  topics.push('tasmota/tele/+/STATE');

  const handleMqttMessage = (topic, message) => {
    const msgStr = (message?.toString?.() || '').trim();

    // === SONOFF relay ===
    let m = topic.match(/^sonoff\/state\/([^/]+)$/i);
    if (m) {
      const mac = m[1].toUpperCase();
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;
      const isOn = msgStr.toUpperCase() === 'ON';
      setRelayStates(prev => {
        const changed = prev[deviceId] !== isOn;
        if (changed) {
          const now = Date.now();
          const lastLocal = localActionRef.current[deviceId];
          const ignore = lastLocal && now - lastLocal < 4000;
          if (!ignore) {
            const name = idToName[deviceId] || `Appareil ${deviceId}`;
            showToast(deviceId, name, isOn);
          }
        }
        return { ...prev, [deviceId]: isOn };
      });
      return;
    }

    // === SONOFF online/offline ===
    m = topic.match(/^sonoff\/status\/([^/]+)$/i);
    if (m) {
      const mac = m[1].toUpperCase();
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;
      const isOnline = msgStr.toLowerCase() === 'online';
      setOnlineStates(prev => ({
        ...prev,
        [deviceId]: {
          online: isOnline,
          lastSeen: isOnline ? Date.now() : (prev[deviceId]?.lastSeen ?? 0)
        }
      }));
      return;
    }

    // === TASMOTA relay: POWER or RESULT ===
    m = topic.match(/^tasmota\/stat\/([^/]+)\/(POWER|RESULT)$/i);
    if (m) {
      const topicPart = m[1];
      const mac = Object.keys(macToDeviceId).find(macKey =>
        topicPart.toUpperCase().includes(macKey.slice(-6))
      );
      if (!mac) return;
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;
      try {
        const j = JSON.parse(msgStr);
        const pwr = String(j?.POWER || j?.Power || msgStr).toUpperCase();
        if (pwr === 'ON' || pwr === 'OFF') {
          const isOn = pwr === 'ON';
          setRelayStates(prev => {
            const changed = prev[deviceId] !== isOn;
            if (changed) {
              const now = Date.now();
              const lastLocal = localActionRef.current[deviceId];
              const ignore = lastLocal && now - lastLocal < 4000;
              if (!ignore) {
                const name = idToName[deviceId] || `Appareil ${deviceId}`;
                showToast(deviceId, name, isOn);
              }
            }
            return { ...prev, [deviceId]: isOn };
          });
        }
      } catch (e) {
        console.warn('[MQTT][Tasmota] parse error POWER/RESULT:', e);
      }
      return;
    }

    // === TASMOTA periodic STATE ===
m = topic.match(/^tasmota\/tele\/([^/]+)\/STATE$/i);
if (m) {
  try {
    const j = JSON.parse(msgStr);
    const topicPart = m[1];
    const mac = Object.keys(macToDeviceId).find(macKey =>
      topicPart.toUpperCase().includes(macKey.slice(-6))
    );
    if (!mac) return;
    const deviceId = macToDeviceId[mac];
    if (!deviceId) return;
    const pwr = String(j?.POWER || j?.Power || '').toUpperCase();
    if (pwr === 'ON' || pwr === 'OFF') {
      const isOn = pwr === 'ON';
      setRelayStates(prev => {
        const changed = prev[deviceId] !== isOn;
        if (changed) {
          const now = Date.now();
          const lastLocal = localActionRef.current[deviceId];
          const ignore = lastLocal && now - lastLocal < 4000;
          if (!ignore) {
            const name = idToName[deviceId] || `Appareil ${deviceId}`;
            showToast(deviceId, name, isOn);
          }
        }
        return { ...prev, [deviceId]: isOn };
      });
    }
  } catch (e) {
    console.warn('[MQTT][Tasmota] parse error STATE:', e);
  }
  return;
}

    // === TASMOTA online/offline (LWT) ===
    m = topic.match(/^tasmota\/tele\/([^/]+)\/LWT$/i);
    if (m) {
      const topicPart = m[1];
      const mac = Object.keys(macToDeviceId).find(macKey =>
        topicPart.toUpperCase().includes(macKey.slice(-6))
      );
      if (!mac) return;
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;
      const isOnline = msgStr.toUpperCase() === 'ONLINE';
      setOnlineStates(prev => ({
        ...prev,
        [deviceId]: {
          online: isOnline,
          lastSeen: isOnline ? Date.now() : (prev[deviceId]?.lastSeen ?? 0)
        }
      }));
      return;
    }
  };

  const sub = () => {
    topics.forEach(t => client.subscribe(t, err => {
      if (err) console.error('[MQTT] subscribe error:', t, err?.message || err);
    }));
    client.on('message', handleMqttMessage);
  };

  if (client.connected && !client.disconnecting) sub();
  else client.once('connect', sub);

  return () => {
    client.off('message', handleMqttMessage);
    if (client.connected && !client.disconnecting) {
      topics.forEach(t => client.unsubscribe(t, () => {}));
    }
  };
}, [myDevices, macToDeviceId, idToName, showToast]);

// ======= Pop-up ON/OFF =======
useEffect(() => {
  const prev = prevRelayRef.current;
  const now = Date.now();
  Object.keys(relayStates).forEach(idStr => {
    const id = Number(idStr);
    const newVal = relayStates[id];
    const oldVal = prev[id];
    if (typeof newVal === 'boolean' && typeof oldVal === 'boolean' && newVal !== oldVal) {
      const lastLocal = localActionRef.current[id];
      const ignore = lastLocal && now - lastLocal < 4000;
      if (!ignore) {
        const displayName = idToName[idStr] || idToName[id] || `Appareil ${id}`;
        showToast(id, displayName, newVal);
      }
    }
  });
  prevRelayRef.current = relayStates;
}, [relayStates, idToName, showToast]);

// ======= Commande ON/OFF =======
const toggleRelay = (deviceId, currentState) => {
  const dev = flattenDevices(myDevices).find(d => String(d.id) === String(deviceId));
  const mac = normalizeMac(dev?.identifier);
  if (!mac) return;
  const newState = !currentState;
  localActionRef.current[deviceId] = Date.now();
  setRelayStates(prev => ({ ...prev, [deviceId]: newState }));
  const name = idToName[deviceId] || `Appareil ${deviceId}`;
  showToast(deviceId, name, newState);
  fetch(`${API_BASE}/devices/mqtt_send.php?mac=${mac}&state=${newState ? 'ON' : 'OFF'}`, { credentials: 'include' })
    .then(() => {
      setTimeout(() => {
        setRelayStates(prev => (prev[deviceId] !== newState ? { ...prev, [deviceId]: currentState } : prev));
      }, 2000);
    })
    .catch(() => {
      setRelayStates(prev => ({ ...prev, [deviceId]: currentState }));
      alert("Erreur lors de l'envoi de la commande MQTT ON/OFF.");
    });
};

// ======= Suppression d’un appareil =======
const handleDelete = async (id) => {
  if (!window.confirm('Supprimer cet appareil ?')) return;
  await fetch(`${API_BASE}/devices/delete_device.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
    credentials: 'include'
  });
  setMyDevices(prev =>
    prev.map(group => ({ ...group, devices: group.devices.filter(d => d.id !== id) }))
  );
  setSelectedClaimedId(null);
  setDeleteId(null);
  setShowDeleteDialog(false);
  fetchMyDevices();
};

// ======= Liste pour la modale de suppression =======
const deleteOptions = useMemo(() => {
  return flattenDevices(myDevices).map(d => ({
    value: d.id,
    label: `${d.name || d.device_name || `Appareil ${d.id}`} • ${normalizeMac(d.identifier)}`
  }));
}, [myDevices]);


  // ===== Découverte / appairage ===== (inchangé)
  const [unclaimed, setUnclaimed] = useState([]);
  const [loadingUnclaimed, setLoadingUnclaimed] = useState(false);
  const [pairingMac, setPairingMac] = useState(null);
  const [pairMsg, setPairMsg] = useState(null);
  const [manualMac, setManualMac] = useState('');
  const [manualError, setManualError] = useState('');
  const fmtAge = (sec) => {
    if (sec == null) return '';
    const s = Number(sec) || 0;
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}m${r.toString().padStart(2,'0')}`;
  };
  const fetchUnclaimed = useCallback(() => {
    setLoadingUnclaimed(true);
    const url = `${API_BASE}/devices/get_unclaimed_devices.php?minutes=15&ts=${Date.now()}`;
    return fetch(url, {
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then(r => r.json())
      .then(arr => {
        const list = Array.isArray(arr) ? arr : [];
        const byId = {};
        for (const raw of list) {
          const mac = normalizeMac(raw.identifier);
          if (!mac) continue;
          const item = { ...raw, identifier: mac };
          const prev = byId[mac];
          if (!prev) byId[mac] = item;
          else {
            const prevLs = prev.last_seen || '';
            const curLs  = item.last_seen || '';
            if (curLs > prevLs) byId[mac] = item;
          }
        }
        const dedup = Object.values(byId);
        dedup.sort((a,b) => {
          if (a.last_seen && b.last_seen) return a.last_seen > b.last_seen ? -1 : 1;
          if (typeof a.age_sec === 'number' && typeof b.age_sec === 'number') return a.age_sec - b.age_sec;
          return 0;
        });
        setUnclaimed(dedup);
      })
      .catch(e => console.error('[Unclaimed] fetch error:', e))
      .finally(() => setLoadingUnclaimed(false));
  }, []);
  useEffect(() => {
    if (!showWizard) return;
    fetchUnclaimed();
    const iv = setInterval(fetchUnclaimed, 7000);
    return () => clearInterval(iv);
  }, [showWizard, fetchUnclaimed]);

  const doPair = useCallback(async (rawMac) => {
    const mac = normalizeMac(rawMac);
    setPairMsg(null);
    setPairingMac(mac);
    try {
      const body = new URLSearchParams({ identifier: mac });
      const res = await fetch(`${API_BASE}/devices/claim_ack.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body
      });
      const json = await res.json();
      if (!json?.ok) throw new Error(json?.error || 'ack_failed');
      setPairMsg('ACK envoyé. Attente du device (10–30s)…');
      const deadline = Date.now() + 35000;
      let seen = false;
      while (Date.now() < deadline && !seen) {
        await new Promise(r => setTimeout(r, 3000));
        await fetchMyDevices();
        const flat = flattenDevices(myDevicesRef.current);
        seen = flat.some(d => normalizeMac(d.identifier) === mac);
      }
      if (seen) {
        setPairMsg('✅ Appareil ajouté !');
        setTimeout(() => { setShowWizard(false); setPairMsg(null); }, 1500);
      } else {
        setPairMsg("ℹ️ ACK reçu. Le device ne s’est pas encore présenté. Vérifie son alimentation / Wi-Fi.");
      }
    } catch (e) {
      setPairMsg(`❌ Erreur: ${e.message || e}`);
    } finally {
      setPairingMac(null);
      fetchUnclaimed();
    }
  }, [fetchMyDevices, fetchUnclaimed]);

  // ===== Firmware (check + UI + OTA) =====
  const [fwById, setFwById] = useState({});
  const [fwPromptForId, setFwPromptForId] = useState(null);
  const fwLastCheckRef = useRef({});
  const fwByIdRef = useRef({});
  useEffect(() => { fwByIdRef.current = fwById; }, [fwById]);

  const setFwState = (id, patch) => {
    if (!mountedRef.current) return;
    setFwById(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const checkDeviceFw = useCallback(async (device, force = false) => {
    if (!device) return;
    const id = device.id;
    const now = Date.now();
    const last = fwLastCheckRef.current[id] || 0;
    if (!force && now - last < 60_000) return;
    fwLastCheckRef.current[id] = now;

    const fwName = device.firmware_name || device.firmware || '';
    const rawCurrent = device.firmware_version || device.firmware || fwName || '';
    const currentVersion = extractVersion(rawCurrent) || extractVersion(fwName) || '';
    const brand = (device.brand || '').toUpperCase();

    // Les devices Tasmota gèrent leur propre OTA — pas de check côté LDJ
    if (brand === 'TASMOTA') {
      setFwState(id, { checking: false, hasUpdate: false, current: currentVersion });
      return;
    }

    const model = brand === 'LDJ' ? 'LDJ_MiniR2' : (extractModel(fwName) || 'LDJ_MiniR2');

    setFwState(id, { checking: true, error: null, current: currentVersion });

    const updateStateFromLatest = (latest) => {
      if (!latest || !currentVersion) {
        setFwState(id, { checking: false, hasUpdate: false, latest, current: currentVersion });
        return;
      }
      const hasUpdate = cmpVer(latest, currentVersion) > 0;
      setFwState(id, { checking: false, hasUpdate, latest, current: currentVersion });
    };

    try {
      const r1 = await fetch(FW_GET_CURRENT(model), { credentials: 'include', headers: { 'Cache-Control': 'no-cache' } });
      if (r1.ok) { const j1 = await r1.json(); updateStateFromLatest(parseLatestFromResp(j1)); return; }
    } catch {}
    try {
      const r2 = await fetch(FW_GET_LATEST(model), { credentials: 'include', headers: { 'Cache-Control': 'no-cache' } });
      const j2 = await r2.json(); updateStateFromLatest(parseLatestFromResp(j2));
    } catch {
      setFwState(id, { checking: false, error: 'fw_check_failed' });
    }
  }, []);

  useEffect(() => {
    const flat = flattenDevices(myDevices);
    if (!flat.length) return;
    flat.forEach(d => {
      const online = onlineStates[d.id]?.online ?? (Number(d.online) === 1);
      if (online) checkDeviceFw(d);
    });
    const iv = setInterval(() => {
      const again = flattenDevices(myDevicesRef.current);
      again.forEach(d => {
        const online = onlineStates[d.id]?.online ?? (Number(d.online) === 1);
        if (online) checkDeviceFw(d);
      });
    }, FW_CHECK_IV_MS);
    return () => clearInterval(iv);
  }, [myDevices, onlineStates, checkDeviceFw]);

  // MQTT: ota_status + info + claim + ota (fenêtre)
useEffect(() => {
  if (!client) return;
  const topics = [
    'sonoff/ota_status/+',
    'sonoff/info/+',
    'sonoff/claim/',
    'sonoff/claim/+',
    'sonoff/ota/+'
  ];

  const handler = (topic, message, packet) => {
    const msgStr = message?.toString?.() ?? '';

    //
    // --- 1) Réception d'un ordre OTA (ouvrir la fenêtre)
    //
    const mOrder = topic.match(/^sonoff\/ota\/([^/]+)$/i);
    if (mOrder) {
      const mac = String(mOrder[1]).toUpperCase();
      let recent = true;
      try {
        const j = JSON.parse(msgStr);
        if (j?.ts && typeof j.ts === 'number' &&
            packet?.retain &&
            (Date.now() - (j.ts * 1000)) > 60000) {
          recent = false;
        }
      } catch {}
      if (!packet?.retain || recent) openOtaWindow(otaWindowRef, mac);
      return;
    }

    //
    // --- 2) OTA_STATUS : animation barre, progression, états
    //
    const mOta = topic.match(/^sonoff\/ota_status\/([^/]+)$/i);
    if (mOta) {
      const mac = String(mOta[1]).toUpperCase();
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;

      const parsed = parseOtaStatusMessage(msgStr);
      const state  = String(parsed.state || '').toLowerCase();

      // Le firmware n'envoie JAMAIS de vrai pourcentage → pct=0 = indéterminé
      const pct = Number(parsed.progress);
      const hasPct = !isNaN(pct) && pct > 0;

      const now = Date.now();

      // Cas indéterminé (queued, start, pct=0)
      if (!hasPct || state === 'queued' || state === 'start') {
        setFwState(deviceId, {
          updating: true,
          indeterminate: true,
          progress: 0,
          status: 'downloading',
          lastTs: now
        });
        return;
      }

      // Cas rare où un pourcentage réel arriverait
      const mapped = mapOtaProgress(parsed);
      setFwState(deviceId, { ...mapped, lastTs: now });

      // Cas progression = 100% (OTA fini)
      if (mapped.status === 'done' && mapped.progress === 100) {
        setFwState(deviceId, {
          updating: false,
          indeterminate: false,
          progress: 0,
          status: 'done',
          lastTs: now,
          target: null
        });

        const dev = flattenDevices(myDevicesRef.current).find(d => d.id === deviceId);
        if (dev) setTimeout(() => checkDeviceFw(dev, true), 15000);
      }
      return;
    }

    //
    // --- 3) INFO : vérification version après reboot ou normal
    //
    const mInfo = topic.match(/^sonoff\/info\/([^/]+)$/i);
    if (mInfo) {
      const mac = String(mInfo[1]).toUpperCase();
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;

      let info = null;
      try { info = JSON.parse(msgStr); } catch { info = null; }

      const fwName = info?.firmware_name || info?.firmware || '';
      const fwVer  = info?.firmware_version || extractVersion(fwName) || '';
      if (!fwVer) return;

      const state = fwByIdRef.current[deviceId] || {};
      const now = Date.now();

      // Si on vient de finir une OTA
      if (state.target &&
          (fwName === state.target || extractVersion(state.target) === fwVer)) {
        setFwState(deviceId, {
          progress: 0,
          updating: false,
          indeterminate: false,
          status: 'done',
          current: fwVer,
          hasUpdate: false,
          target: null,
          lastTs: now
        });
        return;
      }

      // Si on était en mise à jour et que la version a changé → terminé
      if (state.updating) {
        const latest = state.latest || '';
        if (!latest || cmpVer(fwVer, latest) >= 0) {
          setFwState(deviceId, {
            progress: 0,
            updating: false,
            indeterminate: false,
            status: 'done',
            current: fwVer,
            hasUpdate: false,
            target: null,
            lastTs: now
          });
          return;
        }
      }

      // Mise à jour classique des infos
      setFwState(deviceId, { current: fwVer, lastTs: now });
      return;
    }

    //
    // --- 4) CLAIM : appareil revenu online → OTA terminée
    //
    const mClaim = topic.match(/^sonoff\/claim\/([^/]+)$/i);
    if (mClaim) {
      const mac = String(mClaim[1]).toUpperCase();
      const deviceId = macToDeviceId[mac];
      if (!deviceId) return;

      const s = fwByIdRef.current[deviceId] || {};

      if (s.updating) {
        // OTA terminée : l’appareil a rebooté et annoncé sa nouvelle présence
        setFwState(deviceId, {
          updating: false,
          indeterminate: false,
          progress: 100,
          status: 'done',
          lastTs: Date.now(),
          target: null
        });
      }
      return;
    }
  };

  const sub = () => {
    topics.forEach(t =>
      client.subscribe(t, err => {
        if (err) console.error('[MQTT] subscribe OTA/info/claim err:', t, err?.message || err);
      })
    );
    client.on('message', handler);
  };

  if (client.connected && !client.disconnecting) sub();
  else client.once('connect', sub);

  return () => {
    client?.off('message', handler);
    if (client.connected && !client.disconnecting) {
      topics.forEach(t => client.unsubscribe(t, () => {}));
    }
  };
}, [macToDeviceId, checkDeviceFw]);


  // Watchdog OTA
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      const cur = fwByIdRef.current || {};
      Object.keys(cur).forEach(idStr => {
        const id = Number(idStr);
        const s = cur[id] || {};
        if ((s.updating || (s.progress || 0) > 0 || s.indeterminate) && s.lastTs && (now - s.lastTs) > 120000) {
          setFwState(id, { updating: false, progress: 0, indeterminate: false, status: 'done' });
        }
      });
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  // (Optionnel – HTTP polling si un endpoint existe)
  const pollOtaProgress = useCallback(async (device) => {
    if (!FW_OTA_PROGRESS) return;
    const id = device.id;
    const start = Date.now();
    const MAX_MS = 5 * 60 * 1000;
    const STEP_MS = 3000;
    const tryFetchOnce = async () => {
      try {
        const r = await fetch(`${FW_OTA_PROGRESS}?identifier=${encodeURIComponent(normalizeMac(device.identifier))}&ts=${Date.now()}`, {
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (!r.ok) return null;
        return await r.json();
      } catch { return null; }
    };
    while (mountedRef.current && Date.now() - start < MAX_MS) {
      const j = await tryFetchOnce();
      if (!j) break;
      const status = j.status || j.state || '';
      const progress = Number(j.progress ?? j.percent ?? j.percentage ?? 0);
      const indet = !(Number.isFinite(progress)) || progress <= 0;
      setFwState(id, { updating: true, progress: Math.max(0, Math.min(100, progress || 0)), indeterminate: indet, status });
      if (status === 'done' || status === 'success' || progress >= 100) {
        setFwState(id, { updating: false, progress: 0, indeterminate: false, status: 'done' });
        const dev = flattenDevices(myDevicesRef.current).find(d => d.id === id);
        if (dev) setTimeout(() => checkDeviceFw(dev, true), 15000);
        return;
      }
      if (status === 'error' || status === 'failed') {
        setFwState(id, { updating: false, indeterminate: false, status: 'error', error: 'ota_failed' });
        return;
      }
      await new Promise(r => setTimeout(r, STEP_MS));
    }
  }, [checkDeviceFw]);

  const triggerOta = useCallback(async (device) => {
    if (!device) return { ok: false, error: 'missing_device' };
    const id = device.id;
    const mac = normalizeMac(device.identifier);
    const fwName = device.firmware_name || device.firmware || '';
    const model = extractModel(fwName) || 'LDJ_MiniR2';

    setFwState(id, { checking: true, error: null });

    let ok = false, j = null;
    try {
      let res = await fetch(FW_REQUEST_OTA, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: id, identifier: mac, model })
      });
      j = await res.json().catch(() => ({}));
      ok = (typeof j?.ok === 'undefined') ? res.ok : !!j.ok;
      if (!ok) {
        res = await fetch(FW_REQUEST_OTA, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ device_id: String(id), identifier: mac, model })
        });
        j = await res.json().catch(() => ({}));
        ok = (typeof j?.ok === 'undefined') ? res.ok : !!j.ok;
      }
    } catch {}

    const target = (fwByIdRef.current[id]?.latest) || null;
    // IMPORTANT: démarrer en indéterminé (spinner) — pas de faux "5 %"
    setFwState(id, { checking: false, updating: ok, progress: ok ? 0 : 0, indeterminate: !!ok, status: ok ? 'starting' : undefined, target });
    if (ok) {
      openOtaWindow(otaWindowRef, mac);
      pollOtaProgress(device); // (ne fait rien si FW_OTA_PROGRESS = null)
      const dev = device;
      setTimeout(() => checkDeviceFw(dev, true), 15000);
      return { ok: true };
    }
    return { ok: false, error: 'ota_trigger_failed', details: j?.error || j?.msg };
  }, [pollOtaProgress, checkDeviceFw]);

  // ==== Membres du foyer ====
  const fetchMembers = useCallback(async () => {
    if (isGuest) return;
    try {
      const res = await fetch(`${API_BASE}/user/list_members.php`, { credentials: "include" });
      const data = await res.json();
      if (data.success) setMembers(data.members || []);
    } catch {}
  }, [isGuest]);

  useEffect(() => {
    if (accountOpen && !isGuest) fetchMembers();
  }, [accountOpen, isGuest, fetchMembers]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setLoadingInvite(true);
    setInviteStatus("");
    try {
      const res = await fetch(`${API_BASE}/user/invite_guest.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: inviteEmail.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setInviteStatus("✅ " + (data.message || "Invitation envoyée"));
        setInviteEmail("");
        fetchMembers();
      } else {
        setInviteStatus("❌ " + (data.error || "Erreur"));
      }
    } catch {
      setInviteStatus("❌ Erreur réseau");
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleRevoke = async (member_id, memberName) => {
    if (!window.confirm(`Supprimer le compte invité de ${memberName} ? Cette action supprime définitivement son compte.`)) return;
    try {
      const res = await fetch(`${API_BASE}/user/revoke_member.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ member_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setMembers(prev => prev.filter(m => m.member_id !== member_id));
      } else {
        alert(data.error || "❌ Erreur lors de la révocation");
      }
    } catch {
      alert("❌ Erreur réseau");
    }
  };

  // ==== Chargement infos compte (depuis /user/* ; fallback props user) ====
  const loadAccount = useCallback(async () => {
    setAccountLoading(true);
    try {
      // 1) essai endpoint me.php (recommandé)
      const candidates = [
        `${API_BASE}/user/me.php`,
        `${API_BASE}/user/get_profile.php`,
        `${API_BASE}/auth/check_session.php`,
        `${API_BASE}/user/profile.php`,
      ];
      let got = null;
      for (const url of candidates) {
        try {
          const { ok, json } = await fetchJSON(url);
          if (!ok || !json) continue;
          // différents formats possibles
          const u = json.user || json.data || json;
          const username = u.username || u.name || '';
          const email    = u.email || '';
          const created  = u.created_at || '';
          if (username || email) { got = { username, email, created_at: created }; break; }
        } catch {}
      }
      // 2) fallback immédiat sur props user (renvoyé par login.php)
      if (!got) {
        got = {
          username: user?.username || user?.name || '',
          email: user?.email || '',
          created_at: user?.created_at || ''
        };
      }
      setAccount({
        username: got.username || '—',
        email: got.email || '—',
        created_at: got.created_at || '—'
      });
    } finally {
      setAccountLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (accountOpen) loadAccount();
  }, [accountOpen, loadAccount]);

  const editingDevice = editingId
    ? flattenDevices(myDevices).find(d => d.id === editingId)
    : null;

  // ===== Commande vocale via l'arbre =====
  const startVoiceCommand = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceText("Navigateur non compatible (utilisez Chrome ou Safari)");
      setTimeout(() => setVoiceText(''), 3000);
      return;
    }
    if (isListening) return;

    const rec = new SR();
    voiceRecRef.current = rec;
    rec.lang = 'fr-FR';
    rec.interimResults = false;
    rec.maxAlternatives = 3;

    setIsListening(true);
    setVoiceText("Écoute...");

    const norm = s => s.toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9 ]/g, ' ').trim();

    rec.onresult = (e) => {
      const raw = e.results[0][0].transcript;
      const t = norm(raw);
      setVoiceText('"' + raw + '"');

      const isOn  = /allume|active|lance/.test(t);
      const isOff = /etein|eteint|coupe|arrete|stop/.test(t);

      if (!isOn && !isOff) {
        setVoiceText('Commande non reconnue — dites "allume" ou "éteint"');
        setTimeout(() => setVoiceText(''), 3000);
        return;
      }

      const flat = flattenDevices(myDevices);

      if (/\btout\b/.test(t)) {
        flat.forEach(d => toggleRelay(d.id, isOn ? true : false));
        setVoiceText(isOn ? "Tout allumé" : "Tout éteint");
        setTimeout(() => setVoiceText(''), 2500);
        return;
      }

      // Supprime mots-clés de commande + articles
      const cleaned = t
        .replace(/\b(allume|etein|eteint|active|coupe|arrete|stop|la|le|les|l|mon|ma|mes)\b/g, ' ')
        .replace(/\s+/g, ' ').trim();

      // Trouve le device dont le nom correspond le mieux
      let bestDevice = null, bestScore = 0;
      flat.forEach(d => {
        const name = norm(d.name || d.device_name || '');
        if (!name) return;
        const nameWords = name.split(' ').filter(w => w.length > 1);
        const score = nameWords.filter(w => cleaned.includes(w)).length
                    + (cleaned.includes(name) ? 10 : 0);
        if (score > bestScore) { bestScore = score; bestDevice = d; }
      });

      if (bestDevice && bestScore > 0) {
        const devName = bestDevice.name || bestDevice.device_name;
        toggleRelay(bestDevice.id, isOn ? true : false);
        setVoiceText((isOn ? "Allumé" : "Éteint") + " — " + devName);
      } else {
        setVoiceText('Appareil non trouvé : "' + cleaned + '"');
      }
      setTimeout(() => setVoiceText(''), 3000);
    };

    rec.onerror = (e) => {
      setVoiceText(e.error === 'no-speech' ? 'Rien entendu' : 'Erreur : ' + e.error);
      setTimeout(() => setVoiceText(''), 2500);
      setIsListening(false);
    };

    rec.onend = () => setIsListening(false);

    rec.start();
  }, [isListening, myDevices, toggleRelay]);

  const renderOtaInline = (fw) => {
  if (!(fw.updating || fw.indeterminate || fw.progress > 0)) return null;

  return (
    <div className={`progress-out ${fw.indeterminate ? 'indeterminate' : ''}`}>
      <div
        className="progress-in"
        style={{
          width: fw.indeterminate ? '30%' : `${Math.max(0, Math.min(100, fw.progress || 0))}%`
        }}
      />
    </div>
  );
};

  return (
    <div className="page">
      <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
        <img
          className="logo"
          src="/logo-fond-transparent.png"
          alt="Logo Lumieres de Jardin"
          onClick={startVoiceCommand}
          title={isListening ? "Écoute en cours..." : "Appuyer pour commande vocale"}
          style={{
            cursor: 'pointer',
            transition: 'filter 0.3s, transform 0.3s',
            filter: isListening ? 'drop-shadow(0 0 14px #00FFA3)' : 'none',
            transform: isListening ? 'scale(1.08)' : 'scale(1)',
            display: 'block'
          }}
        />
        {voiceText && (
          <div style={{
            position: 'absolute', bottom: -28, left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.78)', color: '#00FFA3',
            padding: '3px 12px', borderRadius: 20,
            fontSize: 12, whiteSpace: 'nowrap', zIndex: 200,
            pointerEvents: 'none'
          }}>
            {isListening ? '🎤 ' : ''}{voiceText}
          </div>
        )}
      </div>
      <div className="title-row">
        <h2 className="title">LUMIERES DE JARDIN de {user?.username || ''}</h2>
        {!accountOpen && (
          <button className="account-btn" onClick={() => setAccountOpen(true)} title="Mon compte" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}>
            <FaUserCircle size={28} color="#056b4d" />
          </button>
        )}
      </div>

      {/* ===== Modale “Mon compte” centrée ===== */}
      {accountOpen && (
        <div className="overlay overlay--center" onClick={() => setAccountOpen(false)}>

      {/* ===== Modale changement mot de passe ===== */}
      {showPwModal && (
        <div className="overlay overlay--center" onClick={() => setShowPwModal(false)}>
          <div className="card card--narrow" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPwModal(false)} aria-label="Fermer">✖</button>
            <h3>Changer le mot de passe</h3>
            <div className="row"><span className="row-label">Mot de passe actuel :</span>
              <input type="text" className="row-input" value={oldPw} onChange={e => setOldPw(e.target.value)} />
            </div>
            <div className="row"><span className="row-label">Nouveau :</span>
              <input type="text" className="row-input" value={newPw} onChange={e => setNewPw(e.target.value)} />
            </div>
            <div className="row"><span className="row-label">Confirmer :</span>
              <input type="text" className="row-input" value={newPw2} onChange={e => setNewPw2(e.target.value)} />
            </div>
            <div className="dialog-actions" style={{marginTop: 12}}>
              <button className="btn btn-light" onClick={() => setShowPwModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={async () => {
                if (newPw !== newPw2) { alert('Les mots de passe ne correspondent pas'); return; }
                if (!oldPw || !newPw) { alert('Champs manquants'); return; }
                try {
                  // 1) Essai JSON
                  let ok = false, msg = '';
                  try {
                    const r = await fetch(`${API_BASE}/user/change_password.php`, {
                      method:'POST',
                      credentials:'include',
                      headers:{'Content-Type':'application/json'},
                      body: JSON.stringify({ old_password: oldPw, new_password: newPw, confirm_password: newPw2 })
                    });
                    ok = r.ok;
                    try { const j = await r.json(); if (j?.ok === false) { ok = false; msg = j?.error || ''; } } catch {}
                  } catch(e) {}
                  // 2) Fallback x-www-form-urlencoded (très courant en PHP)
                  if (!ok) {
                    try {
                      const r2 = await fetch(`${API_BASE}/user/change_password.php`, {
                        method:'POST',
                        credentials:'include',
                        headers:{'Content-Type':'application/x-www-form-urlencoded'},
                        body: new URLSearchParams({ old_password: oldPw, new_password: newPw, confirm_password: newPw2 }).toString()
                      });
                      ok = r2.ok;
                      try { const j2 = await r2.json(); if (j2?.ok === false) { ok = false; msg = j2?.error || ''; } } catch {}
                    } catch(e) {}
                  }
                  if (ok) {
                    alert('Mot de passe mis à jour.');
                    setShowPwModal(false);
                    setOldPw(''); setNewPw(''); setNewPw2('');
                  } else {
                    alert(msg || "Échec du changement de mot de passe. Vérifie l'actuel.");
                  }
                } catch(e) {
                  alert("Erreur réseau / serveur lors du changement de mot de passe");
                }
              }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
>
          <div className="card card--narrow" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setAccountOpen(false)} aria-label="Fermer">✖</button>
            <h3>Mon compte</h3>

            <div className="row"><span className="row-label">Nom d’utilisateur :</span><span>{accountLoading ? "..." : account.username || "-"}</span></div>
            <div className="row"><span className="row-label">E-mail :</span><span>{accountLoading ? "..." : account.email || "-"}</span></div>
            <div className="row"><span className="row-label">Créé le :</span><span>{accountLoading ? "..." : safeToLocaleDate(account.created_at)}</span></div>

            {isGuest && (
              <p style={{ fontSize: 13, color: "#aaa", margin: "10px 0 0", textAlign: "center" }}>
                Compte invité — vous pouvez contrôler les appareils
              </p>
            )}

            {!isGuest && (
              <div style={{ marginTop: 16, borderTop: "1px solid #333", paddingTop: 14 }}>
                <button
                  className="btn btn-light"
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", marginBottom: 8 }}
                  onClick={() => setShowMembersSection(v => !v)}
                >
                  <span>👥 Utilisateurs Autorisés</span>
                  <span style={{ fontSize: 12 }}>{showMembersSection ? "▲" : "▼"}</span>
                </button>

                {showMembersSection && (
                  <div>
                    {members.length === 0 ? (
                      <p style={{ fontSize: 13, color: "#aaa", margin: "4px 0 10px" }}>
                        Aucun membre pour l’instant
                      </p>
                    ) : (
                      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 10px" }}>
                        {members.map(m => (
                          <li key={m.member_id} style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "5px 0", borderBottom: "1px solid #333", fontSize: 13
                          }}>
                            <span style={{ color: "#111", fontSize: 15, fontWeight: 500 }}>
                              {m.username}{m.email ? ` (${m.email})` : ""}
                            </span>
                            <button
                              onClick={() => handleRevoke(m.member_id, m.username || m.email)}
                              style={{
                                background: "none", border: "none", color: "#e55",
                                cursor: "pointer", fontSize: 16, padding: "0 4px"
                              }}
                              title="Révoquer l’accès"
                            >✖</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="email"
                        placeholder="Email à inviter"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleInvite()}
                        style={{
                          flex: 1, padding: "7px 10px", borderRadius: 6,
                          border: "1px solid #555", background: "#1a1a1a",
                          color: "#fff", fontSize: 13
                        }}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={handleInvite}
                        disabled={loadingInvite || !inviteEmail.trim()}
                        style={{ padding: "7px 12px", fontSize: 13 }}
                      >
                        {loadingInvite ? "..." : "Inviter"}
                      </button>
                    </div>
                    {inviteStatus && (
                      <p style={{ fontSize: 13, marginTop: 6, color: inviteStatus.startsWith("✅") ? "#111" : "#e55", fontWeight: 500 }}>
                        {inviteStatus}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowPwModal(true)}
              >
                Changer le mot de passe
              </button>
            </div>

            <div className="dialog-actions" style={{ marginTop: 14 }}>
              <button className="btn btn-light" onClick={() => setAccountOpen(false)}>Fermer</button>
              <button
                className="btn btn-warning"
                onClick={async () => {
                  try {
                    await fetch(`${API_BASE}/user/logout.php`, { method: 'POST', credentials: 'include' });
                  } catch {}
                  if (onLogout) onLogout();
                  window.location.href = '/';
                }}
              >
                Déconnexion
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setDelStep(1)}
              >
                Supprimer le compte
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Suppression de compte — modal 3 étapes ===== */}
      {delStep > 0 && (
        <div className="overlay overlay--center" onClick={resetDel}>
          <div className="card card--narrow" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <button className="modal-close" onClick={resetDel}>✖</button>

            {/* Étape 1 : avertissement */}
            {delStep === 1 && (
              <>
                <h3 style={{ color: "#c00", marginBottom: 12 }}>⚠️ Supprimer mon compte ?</h3>
                <p style={{ marginBottom: 8 }}>Cette action est <strong>irréversible</strong>. Elle entraînera :</p>
                <ul style={{ fontSize: 14, lineHeight: 1.7, paddingLeft: 18, marginBottom: 16 }}>
                  <li>La perte d'accès à <strong>tous vos appareils</strong></li>
                  <li>La suppression de tous vos <strong>plannings</strong></li>
                  <li>La suppression de toutes vos <strong>localisations</strong></li>
                  <li>La révocation de l'accès de vos <strong>utilisateurs autorisés</strong></li>
                </ul>
                <p style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>
                  Vos appareils physiques ne seront pas affectés, mais ils ne seront plus pilotables depuis l'application.
                </p>
                <div className="dialog-actions">
                  <button className="btn btn-light" onClick={resetDel}>Annuler</button>
                  <button className="btn btn-danger" onClick={() => setDelStep(2)}>Continuer →</button>
                </div>
              </>
            )}

            {/* Étape 2 : frappe de confirmation */}
            {delStep === 2 && (
              <>
                <h3 style={{ color: "#c00", marginBottom: 12 }}>Confirmer la suppression</h3>
                <p style={{ fontSize: 14, marginBottom: 16 }}>
                  Pour confirmer, tapez exactement <strong>SUPPRIMER</strong> dans le champ ci-dessous :
                </p>
                <input
                  type="text"
                  value={delTyped}
                  onChange={e => setDelTyped(e.target.value)}
                  placeholder="SUPPRIMER"
                  autoFocus
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 7, fontSize: 15,
                    border: delTyped === "SUPPRIMER" ? "2px solid #c00" : "1.5px solid #aaa",
                    boxSizing: "border-box", marginBottom: 16, letterSpacing: 1
                  }}
                />
                <div className="dialog-actions">
                  <button className="btn btn-light" onClick={() => { setDelStep(1); setDelTyped(""); }}>← Retour</button>
                  <button
                    className="btn btn-danger"
                    disabled={delTyped !== "SUPPRIMER"}
                    onClick={() => setDelStep(3)}
                    style={delTyped !== "SUPPRIMER" ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                  >
                    Confirmer →
                  </button>
                </div>
              </>
            )}

            {/* Étape 3 : mot de passe */}
            {delStep === 3 && (
              <>
                <h3 style={{ color: "#c00", marginBottom: 12 }}>Dernière vérification</h3>
                <p style={{ fontSize: 14, marginBottom: 16 }}>
                  Entrez votre mot de passe pour finaliser la suppression définitive de votre compte :
                </p>
                {delError && (
                  <p style={{ color: "#c00", fontSize: 13, marginBottom: 10 }}>❌ {delError}</p>
                )}
                <input
                  type="password"
                  value={delPassword}
                  onChange={e => setDelPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  autoFocus
                  style={{
                    width: "100%", padding: "10px 12px", borderRadius: 7, fontSize: 15,
                    border: "1.5px solid #aaa", boxSizing: "border-box", marginBottom: 16
                  }}
                  onKeyDown={e => { if (e.key === "Enter" && delPassword && !delLoading) e.currentTarget.closest(".dialog-actions")?.querySelector(".btn-danger")?.click(); }}
                />
                <div className="dialog-actions">
                  <button className="btn btn-light" onClick={() => { setDelStep(2); setDelPassword(""); setDelError(""); }}>← Retour</button>
                  <button
                    className="btn btn-danger"
                    disabled={delLoading || !delPassword}
                    onClick={async () => {
                      setDelLoading(true); setDelError("");
                      try {
                        const res = await fetch(`${API_BASE}/user/delete_account.php`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ password: delPassword }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (data.success) {
                          resetDel();
                          if (onLogout) onLogout();
                          window.location.href = "/";
                        } else {
                          setDelError(data.error || "Erreur lors de la suppression");
                        }
                      } catch {
                        setDelError("Erreur réseau");
                      } finally {
                        setDelLoading(false);
                      }
                    }}
                  >
                    {delLoading ? "Suppression..." : "Supprimer définitivement"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Édition nom/adresse device */}
      {editingDevice && (
        <div className="overlay" onClick={() => setEditingId(null)}>
          <div className="card card--edit" onClick={e => e.stopPropagation()}>
            <form
              className="ldj-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const payload = { ...editFields, id: editingId };
                try {
                  await fetch(`${API_BASE}/devices/update_device_infos.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    credentials: 'include'
                  });
                  setMyDevices(prev => prev.map(group => ({
                    ...group,
                    devices: group.devices.map(d =>
                      d.id === editingId
                        ? {
                            ...d,
                            name: payload.name ?? d.name,
                            home_name: payload.home_name ?? d.home_name,
                            location_name: payload.location_name ?? d.location_name,
                            city_name: payload.city_name ?? d.city_name,
                            code_postal: payload.code_postal ?? d.code_postal,
                            country: payload.country ?? d.country
                          }
                        : d
                    )
                  })));
                } finally {
                  setEditingId(null);
                  setEditFields({});
                  fetchMyDevices();
                }
              }}
            >
              <h4 className="form-title">Modifier l’appareil</h4>
              {[
                { label: "Appareil", key: "name", placeholder: "Nommez votre appareil" },
                { label: "Maison", key: "home_name", placeholder: "Le nom de votre maison" },
                { label: "Zone", key: "location_name", placeholder: "Zone où se trouve l’appareil" },
                { label: "Ville", key: "city_name", placeholder: "Nécessaire pour Soleil" },
                { label: "Code postal", key: "code_postal", placeholder: "Nécessaire pour Soleil" }
              ].map(({ label, key, placeholder }) => (
                <label className="row" key={key}>
                  <span className="row-label">{label} :</span>
                  <input
                    type="text"
                    value={editFields[key] || ''}
                    onChange={e => setEditFields(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="row-input"
                  />
                </label>
              ))}

              {/* Pays */}
              <label className="row">
                <span className="row-label">Pays :</span>
                <div className="row-select">
                  <Select
                    value={COUNTRY_OPTIONS.find(opt => opt.value === (editFields.country || '')) || null}
                    options={COUNTRY_OPTIONS}
                    onChange={opt => setEditFields(f => ({ ...f, country: opt?.value || '' }))}
                    getOptionLabel={opt => (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <FlagIcon code={opt.flag} size={16} />
                        <span>{opt.label}</span>
                      </span>
                    )}
                    getOptionValue={opt => opt.value}
                    isClearable
                    placeholder="Nécessaire pour Soleil"
                  />
                </div>
              </label>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">💾</button>
                <button type="button" className="btn btn-danger" onClick={() => setEditingId(null)}>❌</button>
              </div>
            </form>
          </div>
        </div>
      )}

     {loadingDevices ? (
  <div className="loading">🔍 Chargement de vos appareils...</div>
) : (
  <div className="devices-container">
    {myDevices.map(group => (
      <div className="group" key={group.location_id}>
        {group.devices.map(device => {
          const online =
            onlineStates[device.id]?.online !== undefined
              ? onlineStates[device.id].online
              : Number(device.online) === 1;

          let relay;
          if (typeof relayStates[device.id] !== 'undefined') {
            relay = relayStates[device.id];
          } else if (typeof device.relay_state !== 'undefined') {
            relay = device.relay_state === 1 || device.relay_state === '1';
          }

          const fw = fwById[device.id] || {};
          const infoDotCls = `info-dot${fw.hasUpdate ? ' has-update' : ''}`;

                return (
                  <div className="device-row" key={device.id}>
                    <div className="left-col">
                      <div className="name-line">
                        <button
                          type="button"
                          className="name-btn"
                          title={isGuest ? "Accès restreint — compte invité" : "Nommez votre appareil et entrez la ville et le pays pour qu'il s'ajuste aux horaires du soleil"}
                          style={isGuest ? { cursor: "not-allowed", opacity: 0.6 } : undefined}
                          onClick={isGuest ? undefined : e => {
                            e.stopPropagation();
                            setEditingId(device.id);
                            setEditFields({
                              name: device.name || '',
                              home_name: device.home_name || '',
                              location_name: device.location_name || '',
                              city_name: device.city_name || '',
                              code_postal: device.code_postal || '',
                              country: (device.country ?? '')
                            });
                          }}
                        >
                          <span className="ellipsis">{device.name || device.device_name}</span>
                          {fw.hasUpdate && (
                            <span
                              className={infoDotCls}
                              role="button"
                              title="Nouvelle version disponible"
                              onClick={(e) => { e.stopPropagation(); setFwPromptForId(device.id); }}
                            >i</span>
                          )}
                          {fw.checking && (<span className="fw-spinner" title="Vérification…">⏳</span>)}
                        </button>
                      </div>

                      <div className="meta">
  <img
    className="wifi"
    src={online ? '/img/wifi_online.png' : '/img/wifi_offline.png'}
    alt={online ? 'Appareil en ligne' : 'Appareil hors ligne'}
  />
  <span className={`state ${relay === true ? 'state-on' : 'state-off'}`}>
    {relay === true ? 'Allumé' : relay === false ? 'Éteint' : ''}
  </span>

  {device.city_name && (
    <span style={{ fontSize: 11, color: '#888', marginLeft: 6, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
      📍 {device.city_name}
    </span>
  )}

  {device.brand === 'TASMOTA' && (
    <img
      src='/img/power_plug.png'
      alt="Consommation électrique"
      className="tasmota-icon"
      title="Voir consommation électrique"
      onClick={e => {
        e.stopPropagation();
        setSelectedTasmota(device); // 👈 on ouvre une modale spécifique
      }}
    />
  )}
</div>

                      {renderOtaInline(fw)}
                    </div>

                    <div className="actions">
                      <img
                        className={`power ${online && !fw.updating ? 'clickable' : 'disabled'}`}
                        src={relay ? '/img/power_on.png' : '/img/power_off.png'}
                        alt={relay ? 'On' : 'Off'}
                        onClick={e => {
                          e.stopPropagation();
                          if (online && !fw.updating) toggleRelay(device.id, relay);
                        }}
                      />
                      <button
                        className="btn btn-planning"
                        title="Voir plannings"
                        onClick={e => { e.stopPropagation(); setSelectedClaimedId(device.id); }}
                        disabled={fw.updating}
                      >
                        Plannings
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Modal DeviceCard */}
          {selectedClaimedId && (
            <div className="overlay" onClick={() => setSelectedClaimedId(null)}>
              <div className="card card--device" onClick={e => e.stopPropagation()}>
                <div className="card-padding">
                  <DeviceCard
                    device={flattenDevices(myDevices).find(d => d.id === selectedClaimedId)}
                    relayState={relayStates[selectedClaimedId]}
                    isOnline={onlineStates[selectedClaimedId]?.online}
                    onToggleRelay={toggleRelay}
                    onDelete={() => handleDelete(selectedClaimedId)}
                    onClaimed={() => setSelectedClaimedId(null)}
                    onNameChange={(id, name, home, location) => {
                      setMyDevices(prev =>
                        prev.map(group => ({
                          ...group,
                          devices: group.devices.map(d =>
                            d.id === id ? { ...d, name, home_name: home, location_name: location } : d
                          )
                        }))
                      );
                    }}
                    onClose={() => setSelectedClaimedId(null)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Wizard ajout */}
          {showWizard && (
            <div className="overlay" onClick={() => setShowWizard(false)}>
              <div className="card card--wide" onClick={e => e.stopPropagation()}>
                <div className="wizard-split">
                  <div className="wizard-col wizard-procedure">
                    <NewDeviceWizard
                      onCancel={() => setShowWizard(false)}
                      onDeviceAdded={() => { fetchMyDevices(); setShowWizard(false); }}
                    />
                  </div>
                  <div className="wizard-col wizard-discovery">
                    <div className="wizard-head">
                      <h4>Appareils détectés (à appairer)</h4>
                      <button className="btn btn-light" onClick={fetchUnclaimed} disabled={loadingUnclaimed}>
                        {loadingUnclaimed ? '…' : '🔄'}
                      </button>
                    </div>
                    <p className="wizard-hint">
                      Après <b>appui long 5s</b> puis configuration Wi-Fi, l’appareil apparaît ici.
                      Clique <b>Appairer</b> pour l’ajouter à ton compte.
                    </p>
                    {pairMsg && <div className="callout info">{pairMsg}</div>}
                    <div className="detected-list">
                      {loadingUnclaimed ? (
                        <div className="muted">Chargement…</div>
                      ) : unclaimed.length === 0 ? (
                        <div className="muted">Aucun appareil en attente (dernières 15 minutes).</div>
                      ) : (
                        unclaimed.map(u => {
                          const mac = normalizeMac(u.identifier);
                          return (
                            <div className="detected-item" key={mac}>
                              <div className="detected-meta">
                                <div className="detected-mac">{mac}</div>
                                <div className="detected-sub">
                                  {u.firmware ? `FW: ${u.firmware}` : ''} {u.ip ? `• IP: ${u.ip}` : ''} {u.age_sec != null ? `• vu il y a ${fmtAge(u.age_sec)}` : ''}
                                </div>
                              </div>
                              <button
                                className="btn btn-primary"
                                onClick={() => doPair(mac)}
                                disabled={pairingMac === mac}
                                title="Associer cet appareil à mon compte"
                              >
                                {pairingMac === mac ? '…' : 'Appairer'}
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Appairage manuel */}
                    <div className="manual">
                      <div className="manual-title">Appairage manuel</div>
                      <div className="manual-row">
                        <input
                          className="row-input"
                          type="text"
                          placeholder="MAC (ex: A1B2C3D4E5F6 ou A1:B2:...)"
                          value={manualMac}
                          onChange={(e) => { setManualError(''); setManualMac(e.target.value); }}
                        />
                        <button
                          className="btn btn-warning"
                          onClick={() => {
                            const n = normalizeMac(manualMac);
                            if (!n || n.length !== 12) { setManualError('MAC invalide (12 hex).'); return; }
                            doPair(n);
                          }}
                          disabled={!!pairingMac}
                          title="Associer par MAC"
                        >
                          Appairer
                        </button>
                      </div>
                      {manualError && <div className="error-text">{manualError}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bas de page : ajouter / supprimer */}
          {!showWizard && (
            <div className="bottom-bar" style={isGuest ? { opacity: 0.4, pointerEvents: "none" } : undefined}>
              <div className="add-box">
                <DeviceListBox
                  label="➕ Nouvel appareil"
                  onClick={() => setShowWizard(true)}
                  isSelected={showWizard}
                />
              </div>

              <button className="btn btn-delete" onClick={() => setShowDeleteDialog(true)}>
                🗑️ Supprimer un appareil
              </button>
            </div>
          )}
        </div>
      )}

      {/* Suppression */}
      {showDeleteDialog && (
        <div className="overlay" onClick={() => setShowDeleteDialog(false)}>
          <div className="card card--narrow" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowDeleteDialog(false)} aria-label="Fermer">✖</button>
            <h3>Supprimer un appareil</h3>
            <p className="muted">Sélectionnez l’appareil à retirer de votre compte. Cette action supprime aussi ses plannings et sa localisation.</p>
            <div className="row-select">
              <Select
                placeholder="Choisir un appareil…"
                options={deleteOptions}
                value={deleteOptions.find(o => o.value === deleteId) || null}
                onChange={opt => setDeleteId(opt ? opt.value : null)}
                isClearable
              />
            </div>
            <div className="dialog-actions">
              <button className="btn btn-light" onClick={() => setShowDeleteDialog(false)}>Annuler</button>
              <button
                className="btn btn-danger"
                onClick={() => { if (!deleteId) { alert('Sélectionnez un appareil.'); return; } handleDelete(deleteId); }}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pop-up ON/OFF */}
      {toast && (
        <div className="overlay overlay--center">
          <div className="toast-card">
            <button className="modal-close" onClick={() => setToast(null)} aria-label="Fermer">✖️</button>
            <div className="toast-title">{toast.name}</div>
            <div className={`toast-state ${toast.isOn ? 'on' : 'off'}`}>
              {toast.isOn ? 'Allumé' : 'Éteint'}
            </div>
          </div>
        </div>
      )}

 {/* Pop-up Firmware */}
{fwPromptForId != null && (() => {
  const flat = flattenDevices(myDevices);
  const dev = flat.find(d => d.id === fwPromptForId);
  const fw = fwById[fwPromptForId] || {};
  if (!dev) return null;
  if (!fw.hasUpdate && !fw.updating && !fw.indeterminate && !(fw.progress > 0)) return null;
  const mac = normalizeMac(dev.identifier);

  const label =
    fw.status === 'error' ? 'Erreur OTA' :
    fw.status === 'reboot' ? 'Redémarrage…' :
    (fw.updating || fw.indeterminate || fw.progress > 0) ? 'Mise à jour en cours…' :
    'Mise à jour disponible';

  return (
    <div className="overlay" onClick={() => setFwPromptForId(null)}>
      <div className="card card--narrow" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setFwPromptForId(null)} aria-label="Fermer">✖</button>
        <h3>Mise à jour du firmware</h3>
        <p className="muted">
          {dev.name || dev.device_name} ({mac})<br/>
          {fw.current ? <>Actuel : <b>{fw.current}</b><br/></> : null}
          {fw.latest ? <>Disponible : <b>{fw.latest}</b><br/></> : null}
        </p>

        {(fw.updating || fw.progress > 0 || fw.indeterminate) && (
          <div className="fw-progress">
            <div className="fw-progress-label">
              {label} {(!fw.indeterminate && fw.progress) ? `${Math.round(fw.progress)}%` : ''}
            </div>
            <div className={`progress-out ${fw.indeterminate ? 'indeterminate' : ''}`}>
              <div
                className="progress-in"
                style={{
                  width: fw.indeterminate
                    ? '30%'
                    : `${Math.max(0, Math.min(100, fw.progress || 0))}%`
                }}
              />
            </div>
          </div>
        )}

        <div className="dialog-actions">
          <button
            className="btn btn-light"
            onClick={() => setFwPromptForId(null)}
            disabled={fw.updating || fw.indeterminate}
          >
            Fermer
          </button>

          <button
            className="btn btn-primary"
            onClick={async () => {
              if (!fw.hasUpdate) return;

              // Démarre immédiatement l'animation indéterminée
              setFwState(dev.id, {
                updating: true,
                indeterminate: true,
                status: 'starting',
                progress: 0
              });

              const r = await triggerOta(dev);

              if (!r.ok) {
                alert("Impossible de déclencher l'OTA (serveur). Réessaie.");
                // Si l'appel échoue, on annule l'état OTA
                setFwState(dev.id, {
                  updating: false,
                  indeterminate: false,
                  progress: 0,
                  status: "error"
                });
              }
            }}
            disabled={!fw.hasUpdate || fw.checking || fw.updating || fw.indeterminate}
            title="Déclencher la mise à jour OTA"
          >
            {fw.checking ? '…' : (fw.updating || fw.indeterminate ? 'En cours…' : 'Mettre à jour')}
          </button>
        </div>
      </div>
    </div>
  );
})()}

{/* Modal Consommation Tasmota */}
{selectedTasmota && (
  <div className="overlay overlay--center" onClick={() => setSelectedTasmota(null)}>
    <div className="card card--narrow" onClick={e => e.stopPropagation()}>
      <button
        className="modal-close"
        onClick={() => setSelectedTasmota(null)}
        aria-label="Fermer"
      >
        ✖
      </button>

      <h3>Consommation électrique</h3>

      {energyData ? (
        <div className="energy-info">
          <div><strong>Puissance actuelle :</strong> {energyData.Power} W</div>
          <div><strong>Tension actuelle :</strong> {energyData.Voltage} V</div>
          <div><strong>Courant actuel :</strong> {energyData.Current} A</div>
          <div><strong>Conso Aujourd’hui :</strong> {energyData.Today} kWh</div>
          <div><strong>Conso Totale :</strong> {energyData.Total} kWh</div>

          {/* 🔹 Mise en service + jours depuis */}
          {energyData.TotalStartTime && (
            <div>
              <strong>Mise en service :</strong>{" "}
              {new Date(energyData.TotalStartTime).toLocaleDateString("fr-FR")}{" "}
              ({energyData.DaysSinceStart} jour
              {energyData.DaysSinceStart > 1 ? "s" : ""})
            </div>
          )}

          {/* Bouton pour afficher la courbe */}
          <div style={{ margin: "10px 0 16px" }}>
            <button
              className="btn btn-chart"
              onClick={async () => {
                setLoadingChart(true);
                setShowConsoChart(true);
                console.log("selectedTasmota:", selectedTasmota);
                try {
                  const resp = await fetch(
                    `/backend/devices/energy_conso_history.php?device_id=${selectedTasmota.id}`
                  );
                  const json = await resp.json();
                  const data =
                    json && json.ok && Array.isArray(json.data) ? json.data : [];
                  setConsoHistory(data);
                } catch (e) {
                  console.error("Erreur fetch history:", e);
                  setConsoHistory([]);
                }
                setLoadingChart(false);
              }}
            >
              Courbe de Conso
            </button>
          </div>

          <div style={{ marginTop: 8 }}>
            <small>Dernière mise à jour : {energyData.UpdatedAt}</small>
          </div>
        </div>
      ) : (
        <p>Aucune donnée reçue pour cette prise.</p>
      )}

      {/* Courbe historique */}
      {showConsoChart && (
        <div className="conso-chart-modal" style={{ margin: "14px 0" }}>
          {/* Boutons de choix de période */}
          <div style={{ marginBottom: 8 }}>
            <button
              className={consoRange === "all" ? "btn btn-primary" : "btn btn-light"}
              onClick={() => setConsoRange("all")}
            >
              Total
            </button>
            <button
              className={consoRange === "week" ? "btn btn-primary" : "btn btn-light"}
              onClick={() => setConsoRange("week")}
              style={{ marginLeft: 4 }}
            >
              7 jours
            </button>
            <button
              className={consoRange === "day" ? "btn btn-primary" : "btn btn-light"}
              onClick={() => setConsoRange("day")}
              style={{ marginLeft: 4 }}
            >
              1 jour
            </button>
          </div>

          {loadingChart ? (
            <div>Chargement des données…</div>
          ) : filteredConsoHistory && filteredConsoHistory.length ? (
            <ConsoChart data={filteredConsoHistory} />
          ) : (
            <div>Aucune donnée de courbe pour ce device.</div>
          )}

          <div style={{ marginTop: 8 }}>
            <button className="btn btn-light" onClick={() => setShowConsoChart(false)}>
              Fermer la courbe
            </button>
          </div>
        </div>
      )}
      {/* FIN AJOUT */}

      <div className="dialog-actions" style={{ marginTop: 14 }}>
        <button className="btn btn-light" onClick={() => setSelectedTasmota(null)}>
          Fermer
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}

export default DashboardPage;

